<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { onBeforeMount } from 'vue'
import { notify } from '@kyvg/vue3-notification'

import { FieldPreference } from '@/types/general'
import { useAuthStore } from '@/stores/auth-store'
import { LicenseType } from '@/types/company'
import { DefaultProductSort, PackagingPreferences, ProductPageStartTab } from '@/types/packaging'
import useForm from '@/hooks/use-form'

import MyRadioButtonGroup from '@/components/my-components/form/MyRadioButtonGroup.vue'
import MyRadioButton from '@/components/my-components/form/MyRadioButton.vue'
import MyCheckbox from '@/components/my-components/form/MyCheckbox.vue'
import MyForm from '@/components/my-components/form/MyForm.vue'
import MyButton from '@/components/my-components/MyButton.vue'

const authStore = useAuthStore()
const { t } = useI18n()

const form = useForm<PackagingPreferences>({
    signature: FieldPreference.Required,
    signatureScanner: FieldPreference.Hidden,
    consignmentNote: FieldPreference.Optional,
    shipmentNumber: FieldPreference.Optional,
    note: FieldPreference.Optional,
    truckNumber: FieldPreference.Optional,
    trailerNumber: FieldPreference.Optional,
    defaultProductSort: DefaultProductSort.Custom,
    productPageStartTab: ProductPageStartTab.Delivered,
    transitLocationTrades: false,
    nilTradesEnabled: false,
    palletReceiptNumber: FieldPreference.Hidden,
    createCustomersInApp: true,
    statementCompanyLocationFilterEnabled: true,
    haulierLoadUnloadEnabled: true,
    nonMypalletReceiverConfirmationEmails: false,
    requireNoteOnProductDifference: false,
    customAppRoleProductsInformationIcon: false,
    bottomPalletHandling: false,
})

async function savePreferences() {
    const response = await form.submit(
        'PUT',
        window.route('packaging.company.preferences.update', { company: authStore.companyId }),
    )

    if (response) {
        authStore.licenses[LicenseType.PackagingModule] = {
            ...authStore.licenses[LicenseType.PackagingModule],
            ...form.data,
        }

        notify({
            title: t('licensePreferencesUpdated'),
            type: 'success',
        })
    }
}
onBeforeMount(() => {
    const prefs: Partial<PackagingPreferences> =
        authStore.licenses[LicenseType.PackagingModule] ?? {}

    form.reset({ ...prefs })
})
</script>

<template>
    <MyForm
        class="flex flex-col lg:flex-row gap-6"
        :errors="form.errors.value"
        @submit.prevent="savePreferences"
    >
        <div class="flex-1 space-y-2">
            <MyRadioButtonGroup
                v-model="form.data.productPageStartTab"
                :label="t('productPageStartTab')"
            >
                <MyRadioButton :label="t('delivered')" :value="ProductPageStartTab.Delivered" />
                <MyRadioButton :label="t('received')" :value="ProductPageStartTab.Received" />
            </MyRadioButtonGroup>

            <MyRadioButtonGroup v-model="form.data.signature" :label="t('signature')">
                <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
            </MyRadioButtonGroup>

            <MyRadioButtonGroup v-model="form.data.consignmentNote" :label="t('consignmentNote')">
                <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
            </MyRadioButtonGroup>

            <MyRadioButtonGroup v-model="form.data.shipmentNumber" :label="t('shipmentNumber')">
                <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
            </MyRadioButtonGroup>

            <MyRadioButtonGroup v-model="form.data.truckNumber" :label="t('truckNumber')">
                <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
            </MyRadioButtonGroup>

            <MyRadioButtonGroup v-model="form.data.trailerNumber" :label="t('trailerNumber')">
                <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
            </MyRadioButtonGroup>

            <MyRadioButtonGroup v-model="form.data.note" :label="t('remark')">
                <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
            </MyRadioButtonGroup>
            <div
                v-if="
                    authStore.hasLicense(LicenseType.DeliveryManagementModule) &&
                    form.data.note === FieldPreference.Optional
                "
                class="flex items-center space-x-2"
            >
                <MyCheckbox
                    v-model="form.data.requireNoteOnProductDifference"
                    :label="t('requireNoteOnProductDifference')"
                />
                <mdi:information v-tooltip="t('requireNoteOnProductDifferenceExplanation')" />
            </div>
        </div>

        <!-- Right column -->
        <div class="flex-1 space-y-2">
            <MyRadioButtonGroup
                v-model="form.data.palletReceiptNumber"
                :label="t('palletReceiptNumber')"
            >
                <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
            </MyRadioButtonGroup>

            <MyRadioButtonGroup
                v-model="form.data.signatureScanner"
                class="relative"
                :label="t('signeeScanner')"
            >
                <MyRadioButton :label="t('visible')" :value="FieldPreference.Optional" />
                <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />

                <template #label>
                    {{ t('signeeScanner') }}
                    <mdi:information
                        v-tooltip="t('signeeScannerExplanation')"
                        class="absolute top-1 right-0"
                    />
                </template>
            </MyRadioButtonGroup>

            <MyRadioButtonGroup
                v-model="form.data.defaultProductSort"
                class="relative"
                :label="t('defaultProductSort')"
            >
                <MyRadioButton :label="t('custom')" :value="DefaultProductSort.Custom" />
                <MyRadioButton :label="t('byUsage')" :value="DefaultProductSort.ByUsage" />
                <template #label>
                    {{ t('defaultProductSort') }}
                    <mdi:information
                        v-tooltip="t('defaultProductSortExplanation')"
                        class="absolute top-1 right-0"
                    />
                </template>
            </MyRadioButtonGroup>

            <MyRadioButtonGroup
                v-model="form.data.customAppRoleProductsInformationIcon"
                class="relative"
                :label="t('customAppRoleProductsInformationIcon')"
            >
                <MyRadioButton :label="t('visible')" :value="true" />
                <MyRadioButton :label="t('hidden')" :value="false" />

                <template #label>
                    {{ t('customAppRoleProductsInformationIcon') }}
                    <mdi:information
                        v-tooltip="t('customAppRoleProductsInformationIconExplanation')"
                        class="absolute top-1 right-0"
                    />
                </template>
            </MyRadioButtonGroup>

            <MyRadioButtonGroup
                v-model="form.data.statementCompanyLocationFilterEnabled"
                class="relative"
                :label="t('statementCompanyLocationFilterEnabled')"
            >
                <MyRadioButton :label="t('visible')" :value="true" />
                <MyRadioButton :label="t('hidden')" :value="false" />
                <template #label>
                    {{ t('statementCompanyLocationFilterEnabled') }}
                    <mdi:information
                        v-tooltip="t('statementCompanyLocationFilterEnabledDescription')"
                        class="absolute top-1 right-0"
                    />
                </template>
            </MyRadioButtonGroup>

            <MyRadioButtonGroup
                v-model="form.data.createCustomersInApp"
                class="relative"
                :label="t('allowCustomerCreationInApp')"
            >
                <MyRadioButton :label="t('enabled')" :value="true" />
                <MyRadioButton :label="t('disabled')" :value="false" />

                <template #label>
                    {{ t('allowCustomerCreationInApp') }}
                    <mdi:information
                        v-tooltip="t('allowCustomerCreationInAppExplanation')"
                        class="absolute top-1 right-0"
                    />
                </template>
            </MyRadioButtonGroup>

            <MyRadioButtonGroup
                v-model="form.data.haulierLoadUnloadEnabled"
                class="relative"
                :label="t('haulierLoadUnloadEnabled')"
            >
                <MyRadioButton :label="t('enabled')" :value="true" />
                <MyRadioButton :label="t('disabled')" :value="false" />

                <template #label>
                    {{ t('haulierLoadUnloadEnabled') }}
                    <mdi:information
                        v-tooltip="t('haulierLoadUnloadEnabledExplanation')"
                        class="absolute top-1 right-0"
                    />
                </template>
            </MyRadioButtonGroup>

            <div class="flex items-center space-x-2">
                <MyCheckbox v-model="form.data.transitLocationTrades" :label="t('transitTrades')" />
                <mdi:information v-tooltip="t('transitTradesExplanation')" />
            </div>
            <div v-if="form.data.transitLocationTrades" class="flex items-center space-x-2">
                <MyCheckbox
                    v-model="form.data.nonMypalletReceiverConfirmationEmails"
                    :label="t('receiverConfirmationEmails')"
                />
                <mdi:information v-tooltip="t('receiverConfirmationEmailsExplanation')" />
            </div>
            <div class="flex items-center space-x-2">
                <MyCheckbox v-model="form.data.nilTradesEnabled" :label="t('allowNilTrades')" />
                <mdi:information v-tooltip="t('allowNilTradesExplanation')" />
            </div>
            <div
                v-if="authStore.hasLicense(LicenseType.DeliveryManagementModule)"
                class="flex items-center space-x-2"
            >
                <MyCheckbox
                    v-model="form.data.bottomPalletHandling"
                    :label="t('bottomPalletHandling')"
                />
                <mdi:information v-tooltip="t('bottomPalletHandlingExplanation')" />
            </div>
            <div class="w-full flex justify-end pt-12">
                <MyButton
                    type="submit"
                    scheme="primary"
                    :loading="form.loading.value"
                    :disabled="form.loading.value"
                >
                    {{ t('save') }}
                </MyButton>
            </div>
        </div>
    </MyForm>
</template>
